import axios, { AxiosRequestConfig } from "axios";
import isEqual from "lodash/isEqual";
import * as config from "./config";
import { ACCESS_TOKEN, GLOBAL_FETCH_TIMEOUT, REFRESH_TOKEN, TEMPORARY_TOKEN } from "../../constants/AppVarConstants";
import { ROUTE_SIGNIN_NEW, ROUTE_MAIN } from "../../constants/routes";
import store from "../store";
import { extractApplyBlockedFromApiResponse } from "../../utils/jobApplyValidation";

interface Response {
  code: number;
  message: string;
  errors: any;
  status: number | string;
  data: any;
  result?: any;
  missingSections?: any;
  profileStatus?: any;
}

const APP_CUSTOM_ERROR_FALLBACK = "Oops! Something went wrong, try again later.";

const checkForErrorMsg = (response: Response, ref: any): any => {
  const retObj: any = { error: false, errorMsg: "" };

  const bodyCode = Number(response?.code);
  const httpStatus = Number(ref?.status);
  const statusText = String(response?.status ?? "").toUpperCase();

  const bodyIndicatesError =
    Number.isFinite(bodyCode) && (bodyCode < 200 || bodyCode > 299);
  const httpIndicatesError =
    Number.isFinite(httpStatus) && (httpStatus < 200 || httpStatus > 299);
  const statusIndicatesError =
    statusText === "FORBIDDEN" ||
    statusText === "ERROR" ||
    statusText === "FAIL" ||
    statusText === "FAILED";

  if (bodyIndicatesError || httpIndicatesError || statusIndicatesError) {
    retObj.error = true;
    retObj.code = Number.isFinite(bodyCode) ? bodyCode : httpStatus;
    const msg = response?.message ?? response?.result?.message ?? response?.errors;
    retObj.errorMsg = typeof msg === "string" ? msg : APP_CUSTOM_ERROR_FALLBACK;
  }
  return retObj;
};

const buildApiRejectPayload = (response: Response, errorObj: any) => {
  const blocked = extractApplyBlockedFromApiResponse(response);
  return {
    ...errorObj,
    rawResponse: response,
    ...(blocked ?? {}),
  };
};

const fetchTimeout = async (value: number): Promise<any> => {
  return await new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error(`Request timed out after ${value/1000} seconds. Please check your connection and try again.`));
    }, value);
  });
};

const middleware = async (
  endpoint: string,
  method: any,
  extraHeaders?: any,
  payload?: any,
  isNotJSON?: any,
  allowToken = true,
  useAccessToken = true,
  signal?: AbortSignal
): Promise<any> => {
  return await new Promise((resolve, reject) => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    const tempToken = localStorage.getItem(TEMPORARY_TOKEN);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    const allHeaders = {
      ...extraHeaders,
    };

    if (tempToken && allowToken && useAccessToken) {
      allHeaders.Authorization = `Bearer ${tempToken}`;
    } else if (accessToken && allowToken && useAccessToken) {
      allHeaders.Authorization = `Bearer ${accessToken}`;
    } else if (refreshToken && allowToken && !useAccessToken) {
      allHeaders.Authorization = `Bearer ${refreshToken}`;
    }

    if (typeof window !== "undefined") {
      const headers = new Headers();
      Object.keys(allHeaders).forEach((key) => {
        headers.append(key, allHeaders[key]);
      });

      let responseRef: any = null;

      let body: any;
      if (!isEqual(method, "GET")) {
        body = isNotJSON ? payload : JSON.stringify(payload);
      }
      const state = store.getState();
      Promise.race([
        fetchTimeout(GLOBAL_FETCH_TIMEOUT),
        fetch(endpoint, {
          headers,
          method,
          body,
          signal,
        }),
      ])
        .then(async (res: any) => {
          responseRef = res;
          if (res.status === 401 && tempToken) {
            console.log("401 with tempToken - clearing storage and redirecting to signin");
            localStorage.clear();
            sessionStorage.clear();
            window.location.replace(ROUTE_SIGNIN_NEW);
          } else if ( res.status === 401 && refreshToken && useAccessToken) {
            // Request a new access token using the refresh token
            try {
              const userStore = state?.app?.user?.user;
              console.log(userStore);

              const payload = {
                // "phoneNumber" : userStore?.phoneNumber,
                "refreshToken" : refreshToken
              };
              const refreshResponse = await fetch(config.REFRESH_TOKEN_URL, {
                method: "POST",
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(payload)
              });

              const refreshData = await refreshResponse.json();
              
              // localStorage.setItem(TEMPORARY_TOKEN, refreshData?.result?.accessToken);
              localStorage.setItem(ACCESS_TOKEN, refreshData?.result?.accessToken);
              if(!refreshResponse?.ok || !refreshData?.result?.accessToken){
                localStorage.clear();
                sessionStorage.clear();
                 window.location.replace(ROUTE_MAIN);
                return false;
              }
              const retryReponse = await fetch(endpoint, {
                method,
                headers: {
                  ...allHeaders,
                  Authorization: `Bearer ${refreshData?.result?.accessToken as string}`,
                },
                body,
                signal,
              });
              return await retryReponse.json();
            } catch (error: any) {
              console.warn(error);
            }
          }
          if (typeof res === "object" && endpoint === config.USER_LOGOUT_URL) {
            return res;
          }
          return res.json();
        })
        .then((response) => {
          const errorObj = checkForErrorMsg(response, responseRef);
          if (errorObj.error) {
            reject(buildApiRejectPayload(response, errorObj));
          } else {
            resolve(response);
          }
        })
        .catch((err) => {
          console.warn("API Error:", err);
          // Handle timeout errors specifically
          if (err.message && err.message.includes("timed out")) {
            reject({
              error: true,
              errorMsg: "Request timed out. Please check your connection and try again.",
              code: 408
            });
          } else {
            reject(err);
          }
        });
    } else {
      const config: AxiosRequestConfig = {
        headers: allHeaders,
        url: endpoint,
        data: payload,
        method,
      };
      axios(config)
        .then((response: any) => {
          const { data } = response;
          const errorObj = checkForErrorMsg(data, response);
          if (errorObj.error) {
            reject(buildApiRejectPayload(data, errorObj));
          } else {
            resolve(data);
          }
        })
        .catch((err: any) => {
          console.warn("Axios API Error:", err);
          const responseData = err?.response?.data;
          if (responseData && typeof responseData === "object") {
            const errorObj = checkForErrorMsg(responseData, err?.response);
            if (errorObj.error) {
              reject(buildApiRejectPayload(responseData, errorObj));
              return;
            }
          }
          // Handle timeout errors specifically
          if (err.message && err.message.includes("timed out")) {
            reject({
              error: true,
              errorMsg: "Request timed out. Please check your connection and try again.",
              code: 408
            });
          } else {
            reject(err);
          }
        });
    }
  });
};

export default middleware;
